import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  usePollWorkflowRunUntilCompleted,
  useTriggerWorkflowWithRunIdPoll,
  useWorkflowRuns,
} from "Hooks/Github";
import { useSnackbar } from "notistack";
import React, { SyntheticEvent, useState } from "react";
import { ENV_FULL_NAME } from "Types/env";

import MpsDemoScriptStepper from "../../mps/Stepper";

enum DEMO_SCRIPT_MODE {
  STANDARD = "Standard",
  CLEANUP = "Cleanup",
}

const demoModeOptions = [
  {
    value: DEMO_SCRIPT_MODE.STANDARD,
    tooltip:
      "Orbital events, contacts, activities, draft plan and committed plan are created and a resource propagation is triggered for each plan",
  },
  {
    value: DEMO_SCRIPT_MODE.CLEANUP,
    tooltip:
      "Activities related to the satellite are deleted and all the plans with the given tag are deleted",
  },
];

const LeBourgetDemoSetupScript: React.FC = ({}) => {
  const repositoryName = "capgeminidemo";
  const workflowId = "run-with-release-initial-runtime-data.yml";

  const { enqueueSnackbar } = useSnackbar();
  const [environment, setEnvironment] = useState<ENV_FULL_NAME>(
    ENV_FULL_NAME.DEV
  );

  const [mode, setMode] = useState<DEMO_SCRIPT_MODE>(DEMO_SCRIPT_MODE.STANDARD);

  const [isWorkflowTriggerButtonDisabled, setIsWorkflowTriggerButtonDisabled] =
    useState<boolean>(false);
  const [workflowRunId, setWorkflowRunId] = useState<number | undefined>(
    undefined
  );
  const [isWaitingForNewRun, setIsWaitingForNewRun] = useState(false);

  const { mutateAsync: triggerWorkflow } = useTriggerWorkflowWithRunIdPoll();
  const { refetch, latestRunId } = useWorkflowRuns(
    repositoryName,
    workflowId,
    isWaitingForNewRun
  );

  const runIdToUse = workflowRunId ?? latestRunId;

  const { data: run, dataUpdatedAt: lastRunFetch } =
    usePollWorkflowRunUntilCompleted(repositoryName, runIdToUse);

  const onRunLaunch = async (e: SyntheticEvent) => {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    setIsWaitingForNewRun(true);
    setIsWorkflowTriggerButtonDisabled(true);

    try {
      const newRunId = await triggerWorkflow({
        workflow_id: workflowId,
        repo: repositoryName,
        ref: "main",
        inputs: {
          token: form.leanspacetoken.value,
          environment: environment,
          mode: Object.keys(DEMO_SCRIPT_MODE)[
            Object.values(DEMO_SCRIPT_MODE).indexOf(mode)
          ], // get the key of the enum
        },
      });

      setWorkflowRunId(newRunId);
      setIsWaitingForNewRun(false);
      setIsWorkflowTriggerButtonDisabled(false);

      enqueueSnackbar(
        "LeBourget demo setup script application has been requested, its state will soon be displayed in the progress bar",
        { variant: "success" }
      );
    } catch (err) {
      setIsWaitingForNewRun(false);
      setIsWorkflowTriggerButtonDisabled(false);
      enqueueSnackbar(`Failed to trigger workflow: ${err}`, {
        variant: "error",
      });
    }
  };

  const onWorkflowRefetch = () => {
    refetch();
  };

  return (
    <>
      <Box
        sx={{
          padding: 2,
        }}
      >
        <Typography sx={{ padding: 2 }} variant="h5">
          LeBourget Demo setup script
        </Typography>
        <Typography sx={{ padding: 2 }}>Connection settings</Typography>
        <form onSubmit={onRunLaunch}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              padding: 2,
            }}
          >
            <Tooltip title="Leanspace JWT token, found in the console (settings>API)">
              <TextField
                id="leanspacetoken"
                name="leanspacetoken"
                required
                label="Leanspace token"
                sx={{ paddingRight: 10 }}
              />
            </Tooltip>
            <Tooltip title="On which environment data is generated">
              <FormControl required sx={{ paddingRight: 10 }}>
                <FormLabel id="environment">Environment</FormLabel>
                <RadioGroup
                  row
                  defaultValue={ENV_FULL_NAME.DEV}
                  onChange={(event) =>
                    setEnvironment(event.target.value as ENV_FULL_NAME)
                  }
                >
                  <FormControlLabel
                    value={ENV_FULL_NAME.DEV}
                    control={<Radio />}
                    label={ENV_FULL_NAME.DEV}
                    labelPlacement="bottom"
                  />
                  <FormControlLabel
                    value={ENV_FULL_NAME.PROD}
                    control={<Radio />}
                    label={ENV_FULL_NAME.PROD}
                    labelPlacement="bottom"
                  />
                </RadioGroup>
              </FormControl>
            </Tooltip>

            <FormControl required>
              <FormLabel id="setupScriptMode">Script Mode</FormLabel>
              <RadioGroup
                row
                defaultValue={DEMO_SCRIPT_MODE.STANDARD}
                onChange={(event) =>
                  setMode(event.target.value as DEMO_SCRIPT_MODE)
                }
              >
                {demoModeOptions.map((option) => (
                  <Tooltip
                    key={option.value}
                    title={
                      <Typography variant="body2">{option.tooltip}</Typography>
                    }
                  >
                    <FormControlLabel
                      value={option.value}
                      control={<Radio />}
                      label={option.value}
                      labelPlacement="bottom"
                    />
                  </Tooltip>
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
          <Box sx={{ padding: 2, textAlign: "right" }}>
            <Button
              variant="contained"
              sx={{ marginRight: 10 }}
              onClick={onWorkflowRefetch}
            >
              Re-fetch last workflow run
            </Button>
            <Button
              disabled={isWorkflowTriggerButtonDisabled}
              variant="contained"
              type="submit"
            >
              Execute script
            </Button>
          </Box>
        </form>
      </Box>

      {isWaitingForNewRun ? (
        <Box sx={{ textAlign: "center", padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        run && <MpsDemoScriptStepper run={run} lastRunFetch={lastRunFetch} />
      )}
    </>
  );
};

export default LeBourgetDemoSetupScript;
